import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const featuredSlugs = [
  'kegel-exercise-complete-guide',
  'timed-voiding-training-method',
  'safe-urination-holding-limits',
  'overactive-bladder-natural-remedies',
  'postpartum-bladder-recovery',
  'urinary-health-self-check',
];

const faqItems = [
  {
    question: '憋尿控制训练多久能看到效果？',
    answer: '根据个体差异和训练坚持程度，大多数人通过系统的凯格尔运动和定时排尿训练，在坚持4-6周后开始感受到膀胱控制能力的改善，12周左右可以看到较为明显的效果。关键在于持之以恒，不应急于求成。',
  },
  {
    question: '憋尿对身体有害吗？多长时间憋尿是安全的？',
    answer: '适度憋尿是正常的生理功能，但长期过度憋尿确实存在健康风险。对于正常成人，白天排尿间隔2-4小时是合理的范围。当膀胱容量达到400-600毫升时产生明确排尿感，此时适时排尿是健康的。如需延长憋尿时间，应通过科学的膀胱训练方法逐步提升，而非强行忍耐。',
  },
  {
    question: '凯格尔运动适合所有人吗？',
    answer: '凯格尔运动适合大多数希望改善膀胱控制能力的人群。但以下情况需在医生指导下进行：盆底肌过度紧张（高张型盆底功能障碍）、急性尿路感染期间、盆腔手术后恢复期、以及某些神经源性膀胱疾病患者。建议在开始训练前先进行盆底肌功能评估。',
  },
  {
    question: '尿频到什么程度需要就医？',
    answer: '如果日间排尿超过8次、夜间因排尿醒来超过2次、或出现排尿急迫感非常强烈难以忍耐、以及任何形式的漏尿现象时，建议咨询泌尿科医生。此外，伴随排尿疼痛、血尿、排尿困难等症状时，应尽快就医。',
  },
  {
    question: '饮食对膀胱控制有多大影响？',
    answer: '饮食对膀胱功能的影响相当显著。咖啡因、酒精、辛辣食物和酸性水果等是常见的膀胱刺激物，可能加重尿频和急迫感。限制这些食物和饮品的摄入、增加膳食纤维和保持适量水分摄入，对改善膀胱控制症状有明确帮助。',
  },
  {
    question: '女性产后多久可以开始膀胱训练？',
    answer: '产后盆底训练可从产后2-3天开始进行非常轻柔的盆底肌感知练习（顺产者待会阴伤口疼痛缓解后）。系统的膀胱训练通常建议在产后6周复查确认恢复良好后开始。剖宫产者需待腹部切口愈合后开始。产后3-6个月是盆底功能恢复的黄金窗口期。',
  },
];

const stats = [
  { label: '专业文章', value: '26篇', icon: 'ri-article-line' },
  { label: '知识分类', value: '6大类', icon: 'ri-folder-line' },
  { label: '专家团队', value: '30+人', icon: 'ri-team-line' },
  { label: '受益用户', value: '50万+', icon: 'ri-user-line' },
];

export default function HomePage() {
  const featuredArticles = featuredSlugs
    .map((slug) => articles.find((a) => a.slug === slug))
    .filter(Boolean) as typeof articles;

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '膀胱健康管理网',
    url: 'https://bladderhealth.cn',
    description: '专业解析憋尿控制的科学原理、正确方法与膀胱训练技巧。了解憋尿控制的安全边界、常见误区及改善尿频的实用方案，帮助您掌握健康的排尿管理策略。',
    inLanguage: 'zh-CN',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://bladderhealth.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '膀胱健康管理网',
    url: 'https://bladderhealth.cn',
    description: '专业的膀胱健康科普平台，提供科学的憋尿控制训练方法与排尿健康管理方案。',
    foundingDate: '2020-03',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-6789',
      contactType: 'customer service',
      email: 'contact@bladderhealth.cn',
      availableLanguage: 'Chinese',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <SchemaOrg data={websiteSchema} />
      <SchemaOrg data={orgSchema} />
      <SchemaOrg data={faqSchema} />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20abstract%20medical%20wellness%20background%20with%20flowing%20organic%20shapes%20in%20warm%20green%20and%20beige%20tones%2C%20minimalist%20design%2C%20calm%20healing%20atmosphere%2C%20subtle%20gradients%2C%20clean%20modern%20healthcare%20aesthetic&width=1800&height=900&seq=hero-home-2026&orientation=landscape"
            alt="膀胱健康管理 - 憋尿控制训练"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/35"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl text-background-50 leading-tight max-w-3xl mx-auto">
              憋尿控制完全指南：方法、训练与健康管理
            </h1>
            <p className="mt-4 md:mt-6 text-sm md:text-base text-background-50/85 max-w-2xl mx-auto leading-relaxed">
              专业解析憋尿控制的科学原理、正确方法与膀胱训练技巧，帮助您掌握健康的排尿管理策略
            </p>
            <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category"
                className="px-6 py-3 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览全部文章
              </Link>
              <Link
                to="/category/bladder-control-training"
                className="px-6 py-3 rounded-md bg-background-50/15 text-background-50 font-medium text-sm border border-background-50/25 hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
              >
                开始训练
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-8 md:py-10 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center text-primary-500">
                    <i className={`${stat.icon} text-2xl`}></i>
                  </div>
                  <div className="mt-2 text-xl md:text-2xl font-heading text-foreground-950">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Section */}
        <section className="py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="font-heading text-xl md:text-3xl text-foreground-950">
                知识分类
              </h2>
              <p className="mt-2 text-sm text-foreground-500 max-w-lg mx-auto">
                按主题浏览膀胱健康知识，找到最适合您的训练方案
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/60 rounded-lg p-5 md:p-6 hover:border-primary-200 hover:bg-background-50 transition-all duration-300 cursor-pointer flex items-start gap-4"
                >
                  <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                    <i className={`${cat.icon} text-xl text-primary-600`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-base text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {cat.description}
                    </p>
                    <span className="inline-block mt-2 text-xs text-primary-600 font-medium">
                      {cat.articleCount} 篇文章
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 md:mb-10 gap-3">
              <div>
                <h2 className="font-heading text-xl md:text-3xl text-foreground-950">
                  精选文章
                </h2>
                <p className="mt-1 text-sm text-foreground-500">
                  由专家团队精心编写的膀胱健康科普内容
                </p>
              </div>
              <Link
                to="/category"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {featuredArticles.map((article) => {
                const cat = categories.find((c) => c.slug === article.category);
                return (
                  <ArticleCard
                    key={article.slug}
                    article={article}
                    categoryName={cat?.name || article.category}
                  />
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="font-heading text-xl md:text-3xl text-foreground-950">
                常见问题
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                关于膀胱控制训练，您可能关心的问题
              </p>
            </div>
            <div className="max-w-3xl mx-auto divide-y divide-background-200/60">
              {faqItems.map((item, index) => (
                <details key={index} className="group py-4 md:py-5 cursor-pointer">
                  <summary className="flex items-center justify-between gap-3 text-sm md:text-base font-medium text-foreground-900 list-none">
                    {item.question}
                    <i className="ri-add-line w-5 h-5 flex items-center justify-center text-foreground-400 group-open:hidden"></i>
                    <i className="ri-subtract-line w-5 h-5 flex items-center justify-center text-foreground-400 hidden group-open:block"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    {item.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-16 bg-primary-500">
          <div className="w-full px-4 md:px-6 text-center">
            <h2 className="font-heading text-xl md:text-2xl text-background-50">
              开始您的膀胱健康之旅
            </h2>
            <p className="mt-2 text-sm text-background-50/85 max-w-lg mx-auto">
              科学的训练方法 + 专业的健康指导，让每一次排尿管理都更加从容
            </p>
            <Link
              to="/category"
              className="mt-5 md:mt-6 inline-block px-6 py-3 rounded-md bg-background-50 text-primary-600 font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              立即探索
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}